/**
 * Entry points of the social lookup router. `fetchInstagramQuery`, `fetchInstagramProfile` and
 * `fetchTiktokQuery` call these when SOCIAL_ROUTER_ENABLED is on (default); everything else in
 * the monorepo keeps going through the legacy rotation.
 *
 *   routeSearch(platform, query)   cache → hedged providers → cache write / stale fallback
 *   routeProfile(username)         hedged providers → profile shape | false (not found); throws on failure
 *   routeExists(platform, handle)  TikTok oEmbed existence check
 *
 * 🔴 Browser+edge-safe: static imports are the small providers only; the RapidAPI class wrappers
 *    are imported lazily at call time (StarAPI/TikTok classes import this barrel's parents).
 */
import { Buzzoid } from '../instagram/providers/Buzzoid';
import { IgDirect } from '../instagram/providers/IgDirect';
import { Kicksta } from '../instagram/providers/Kicksta';
import { PathSocial } from '../instagram/providers/PathSocial';
import { Upleap } from '../instagram/providers/Upleap';
import { TOembed } from '../tiktok/providers/TOembed';
import { HighSocialTikTokLane, TikTokBestExperienceLane, TikTokLiveAPILane, TikTokScraper7Lane } from '../tiktok/providers/TikTokLanes';
import { cachedSearch, type CacheSource } from './cache';
import { budgetFor, hedged, type HedgeOptions, type HedgeResult } from './hedge';
import { refundPaidCall, reservePaidCall } from './quota';
import {
	isRouterEnabled,
	listProviders,
	ProviderMiss,
	ProviderSkip,
	registerProvider,
	type Platform,
	type Provider,
	type ProviderRequest,
} from './registry';

export { isRouterEnabled } from './registry';
export { RouterFailure } from './hedge';

/** StarAPI (RapidAPI): the paid last resort. Reached only after every free family failed; its daily
 *  budget reservation (quota.ts) decides whether the call may go out. */
const StarApiLane: Provider = {
	id: 'starapi',
	platform: 'instagram',
	capabilities: ['search_users', 'profile'],
	family: 'rapidapi',
	transport: 'rapidapi',
	requiresToken: false,
	seedLatencyMs: 2540,
	lastResort: true,
	parser: (body) => body,
	execute: async (req: ProviderRequest, signal: AbortSignal) => {
		if (signal.aborted) throw new ProviderSkip('aborted before dispatch'); // nothing reserved, nothing sent
		// Atomic reservation (INCR rapidapi:budget:<UTC day>) BEFORE the paid dispatch; over budget, or Redis down
		// beyond the static fallback cap, skips the lane without counting a failure.
		const reservation = await reservePaidCall();
		if (!reservation.ok) throw new ProviderSkip(`rapidapi daily budget exhausted (${reservation.count}/${reservation.cap}, ${reservation.source})`);
		if (signal.aborted) { // the attempt was cancelled while the reservation was in flight: hand it back
			await refundPaidCall(reservation);
			throw new ProviderSkip('aborted before dispatch');
		}
		// load the barrel first: StarAPI.ts and the barrel import each other, and only barrel-first evaluates in a safe order
		// (matters when a route calls ensureRegistered() without ever importing the barrel)
		await import('../instagram/index');
		const StarAPI = (await import('../instagram/providers/StarAPI')).default;
		const starapi = new StarAPI();
		if (req.capability === 'profile') {
			const profile = await starapi.fetchProfile(req.query, false);
			if (profile === false || profile == null) throw new ProviderMiss('starapi: no such user');
			return profile;
		}
		const rows = await starapi.fetchInstagramQuery(req.query, 'users', req.limit, 0, false);
		if (rows === false || rows == null) return [];
		if (!Array.isArray(rows)) throw new Error('starapi: unexpected search result');
		return rows;
	},
};

/** Flash API answers "User not found" as an error body; that is an answer, not a lane failure. */
const FLASH_NOT_FOUND = /user not found|page not found/i;

/** Flash API (RapidAPI, same pooled key as StarAPI): profile lane in front of StarAPI. Same web_profile_info fields
 *  (counts, privacy, id); its supplier placeholder display name is dropped by IGFlashAPI (cleanFullName). Its "not found"
 *  is a miss, never the final word: StarAPI still confirms before an account is called missing (Flash wrongly misses
 *  some business accounts). No gateway-tier filter: no Outfame caller passes a tier. Off: SOCIAL_PROV_FLASH_ENABLED=false. */
const FlashProfileLane: Provider = {
	id: 'flash',
	platform: 'instagram',
	capabilities: ['profile'],
	family: 'rapidapi',
	transport: 'rapidapi',
	requiresToken: false,
	seedLatencyMs: 900,
	parser: (body) => body,
	execute: async (req: ProviderRequest, signal: AbortSignal) => {
		await import('../instagram/index'); // barrel first (the provider and the barrel import each other)
		const IGFlashAPI = (await import('../instagram/providers/IGFlashAPI')).default;
		let profile: any;
		try {
			profile = await new IGFlashAPI().fetchProfile(req.query, false, { signal });
		} catch (error: any) {
			if (FLASH_NOT_FOUND.test(String(error?.message))) throw new ProviderMiss('flash: no such user');
			throw error;
		}
		if (profile === false || profile == null) throw new ProviderMiss('flash: no such user');
		return profile;
	},
};

/** Profile lookups get room for the free lanes (leader + hedge, 3.5 s cap, plus up to 0.7 s of health read) AND the
 *  StarAPI confirmation after them (3 s cap): 0.7 + 0.25 + 3.5 + 3 = 7.45 s. 6.5 s left StarAPI no room after two hanging
 *  free lanes (the estate-a lanes hang on a handle that does not exist), so a missing account fell to the unbounded legacy
 *  rotation (11-25 s, 1 Nov). Stays under signup's 9 s deadline; `reservePaid` keeps a third free lane from eating it. */
export const PROFILE_OVERALL_BUDGET_MS = 8_000;
/** The hedge options every Instagram profile lookup runs with (a caller's own `hedge` options override them). */
/** Profile lookups start a METERED runner-up (Flash, RapidAPI: 150k calls a month, billed even when cancelled) only once
 *  the leader has gone 1 s without an answer; a free runner-up still starts at 250 ms. The leader (Kicksta, free) answers
 *  most lookups in 0.3-0.8 s, so at 250 ms most lookups also started a Flash call that was then cancelled: ~860 billed
 *  calls a day on 2 Nov 2026 (about a sixth of Flash's daily use), each logged as an AbortError. A leader that fails
 *  fast still fails over at once. */
export const PROFILE_METERED_HEDGE_DELAY_MS = 1_000;
export const PROFILE_HEDGE: HedgeOptions = { overallBudgetMs: PROFILE_OVERALL_BUDGET_MS, reservePaid: true, meteredHedgeDelayMs: PROFILE_METERED_HEDGE_DELAY_MS };

let registered = false;
/** Register every lane once (idempotent). Exported so the probe route can reach StarAPI and the rest too. */
export function ensureRegistered(): void {
	if (registered) return;
	registered = true;
	for (const p of [Kicksta, Buzzoid, Upleap, PathSocial, IgDirect, FlashProfileLane, StarApiLane, TikTokBestExperienceLane, TikTokLiveAPILane, TikTokScraper7Lane, HighSocialTikTokLane, TOembed])
		registerProvider(p);
}

/** Rows every search entry holds (the cache key has no limit). */
export const CACHE_ROWS = 15;

export interface RouteCallOptions {
	limit?: number;
	tier?: ProviderRequest['tier'];
	ignoreCache?: boolean;
	hedge?: HedgeOptions;
}

/** ONE structured line per completed request (no per-attempt output): which lane won, how much work it took, how long.
 *  warn-level on purpose: warn/error are what Vercel's log CLI returns — info lines never reach it, and the per-lane win
 *  counts are the evidence used to tune the lane order (4 Nov). */
function logRequest(platform: Platform, capability: string, started: number, won: string, attempts: number, hedgeFired: boolean, cache: 'hit' | 'miss' | 'stale'): void {
	console.warn(`[router] ${platform}:${capability} won=${won} attempts=${attempts} ms=${Date.now() - started} hedge=${hedgeFired ? 'yes' : 'no'} cache=${cache}`);
}

const CACHE_LABEL: Record<CacheSource, 'hit' | 'miss' | 'stale'> = {
	lru: 'hit', prefix: 'hit', fresh: 'hit', legacy: 'hit', negative: 'hit', live: 'miss', stale: 'stale', 'stale-on-error': 'stale',
};

/** Username search with the two-tier cache in front of the hedged providers. */
export async function routeSearch(
	platform: Platform,
	query: string,
	legacyKey: string,
	opts: RouteCallOptions = {}
): Promise<{ data: unknown[]; source: CacheSource }> {
	ensureRegistered();
	const limit = opts.limit ?? 15;
	// The cache key has no `limit`, so every live fetch asks for at least CACHE_ROWS: a limit=1 caller must never leave a
	// one-row entry behind for the next 24 h. Callers asking for fewer get a slice on the way out.
	const fetchLimit = Math.max(limit, CACHE_ROWS);
	const startedAt = Date.now(); // ONE deadline: cache reads, locks and waits eat into the same budget the providers get (4 s Instagram, 6 s TikTok)
	let live: { won: string; attempts: number; hedge: boolean } | undefined; // set only when THIS call ran the providers
	let cached: { data: unknown[]; source: CacheSource };
	try {
		cached = await cachedSearch({
			platform,
			query,
			legacyKey,
			ignoreCache: opts.ignoreCache,
			fetchLive: async () => {
				try {
					const result = await hedged<unknown[]>(
						listProviders(platform, 'search_users'),
						{ capability: 'search_users', query: query.trim(), limit: fetchLimit, tier: opts.tier },
						{ ...opts.hedge, overallBudgetMs: Math.max(500, (opts.hedge?.overallBudgetMs ?? budgetFor(platform).overallMs) - (Date.now() - startedAt)) }
					);
					live = { won: result.providerId ?? 'none', attempts: result.launched ?? result.attempts.length, hedge: !!result.hedgeFired };
					return Array.isArray(result.result) ? result.result : [];
				} catch (error: any) {
					live = { won: 'none', attempts: error?.launched ?? error?.attempts?.length ?? 0, hedge: !!error?.hedgeFired };
					throw error;
				}
			},
		});
	} catch (error) {
		logRequest(platform, 'search_users', startedAt, 'none', live?.attempts ?? 0, live?.hedge ?? false, 'miss');
		throw error;
	}
	const won = cached.source === 'live' ? (live?.won ?? 'peer') : cached.source === 'stale' || cached.source === 'stale-on-error' ? 'stale' : 'cache';
	logRequest(platform, 'search_users', startedAt, won, live?.attempts ?? 0, live?.hedge ?? false, CACHE_LABEL[cached.source]);
	return limit < cached.data.length ? { ...cached, data: cached.data.slice(0, limit) } : cached;
}

/** Run one uncached capability through the hedge and log the single summary line. */
async function routeUncached<T>(platform: Platform, capability: 'profile' | 'exists', pool: Parameters<typeof hedged>[0], req: ProviderRequest, hedge?: HedgeOptions): Promise<HedgeResult<T>> {
	const startedAt = Date.now();
	try {
		const result = await hedged<T>(pool, req, hedge);
		logRequest(platform, capability, startedAt, result.providerId ?? 'none', result.launched ?? result.attempts.length, !!result.hedgeFired, 'miss');
		return result;
	} catch (error: any) {
		logRequest(platform, capability, startedAt, 'none', error?.launched ?? error?.attempts?.length ?? 0, !!error?.hedgeFired, 'miss');
		throw error;
	}
}

/** After routeProfile threw, does its verdict stand (true), or may fetchInstagramProfile re-walk the unbounded legacy
 *  rotation (false)? It stands for a bounded caller (signup has its own deadline), after a paid StarAPI call (legacy
 *  would call StarAPI again outside the router's paid-call cap), and when the router spent its whole budget on at least
 *  two real attempts (a legacy re-walk there only adds 5-20 s: 1 Nov, missing handles took 11-25 s). A router that never
 *  got going (no lane available, every breaker open) still falls back. */
export function routerVerdictStands(error: any, attemptTimeoutMs: number): boolean {
	return attemptTimeoutMs > 0 || error?.paidDispatched === true || (error?.budgetExhausted === true && (error?.launched ?? 0) >= 2);
}

/** Instagram profile by handle. `false` = authoritatively not found; throws RouterFailure when no provider answered. */
export async function routeProfile(username: string, opts: RouteCallOptions = {}): Promise<any> {
	ensureRegistered();
	// A caller that set its own bounds keeps them; otherwise the wider profile budget lets StarAPI confirm a Flash miss.
	const hedge = { ...PROFILE_HEDGE, ...opts.hedge };
	const result = await routeUncached<any>('instagram', 'profile', listProviders('instagram', 'profile'), { capability: 'profile', query: username.trim(), limit: 1, tier: opts.tier }, hedge);
	return result.found ? result.result : false;
}

/** TikTok profile lookups give the free donor 4 s (measured 1.8-3.3 s), so a stalled donor leaves the legacy rotation ~5 s of a 9 s signup deadline. */
export const TIKTOK_PROFILE_OVERALL_BUDGET_MS = 4_000;
/** Below TikTok's 5.5 s default so a hung donor's timeout is not budget-truncated: it counts against the lane and the breaker can open. */
export const TIKTOK_PROFILE_ATTEMPT_TIMEOUT_MS = 3_500;

/** TikTok profile by handle: free lane first, sequential (no hedge; budgetFor('tiktok') attempt cap, 4 s overall budget).
 *  `false` = every provider that answered said no such user; throws RouterFailure when none answered (or none is enabled). */
export async function routeTiktokProfile(username: string, opts: RouteCallOptions = {}): Promise<any> {
	ensureRegistered();
	const result = await routeUncached<any>('tiktok', 'profile', listProviders('tiktok', 'profile'), { capability: 'profile', platform: 'tiktok', query: username.trim(), limit: 1, tier: opts.tier }, { overallBudgetMs: TIKTOK_PROFILE_OVERALL_BUDGET_MS, attemptTimeoutMs: TIKTOK_PROFILE_ATTEMPT_TIMEOUT_MS, ...opts.hedge });
	return result.found ? result.result : false;
}

/** Does this handle exist? (TikTok oEmbed.) null = not found; throws RouterFailure when it could not be determined. */
export async function routeExists(platform: Platform, handle: string, opts: RouteCallOptions = {}): Promise<unknown | null> {
	ensureRegistered();
	const result = await routeUncached<unknown>(platform, 'exists', listProviders(platform, 'exists'), { capability: 'exists', query: handle.trim(), limit: 1 }, opts.hedge);
	return result.found ? result.result : null;
}
